import React, { useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";

const StoryFeed = () => {
  const totalStories = [...Array(11).keys()]; // 총 스토리 배열
  const [stories, setStories] = useState(totalStories); // 전체 스토리 배열
  const [visibleStories, setVisibleStories] = useState(stories.slice(0, 6)); // 처음 보이는 스토리 배열
  const [startIndex, setStartIndex] = useState(0); // 시작 인덱스
  const [borderlessIndexes, setBorderlessIndexes] = useState([]); // 클릭하여 보더가 없는 스토리 인덱스 배열
  const [selectedStory, setSelectedStory] = useState(null); // 클릭된 스토리 인덱스 (팝업용)
  const [preloadedImages, setPreloadedImages] = useState({}); // 사전 로딩된 이미지 저장
  const [loading, setLoading] = useState(true); // 로딩 상태

  // 이미지 사전 로딩
  useEffect(() => {
    const preloadImages = () => {
      let loadedCount = 0;
      totalStories.forEach((index) => {
        const img = new Image();
        img.src = `https://dongsooo.github.io/imgs/dongstgram_img/02_story_contents_img${index + 1}.jpg`;
        img.onload = () => {
          setPreloadedImages((prev) => ({ ...prev, [index]: img.src }));
          loadedCount++;
          if (loadedCount === totalStories.length) {
            setLoading(false); // 모든 이미지가 로드되면 로딩 상태를 false로 설정
          }
        };
      });
    };

    preloadImages();
  }, []); // totalStories 제거

  const updateVisibleStories = useCallback(
    (newStartIndex) => {
      const newVisibleStories = stories
        .slice(newStartIndex, newStartIndex + 6)
        .concat(
          stories.slice(0, Math.max(newStartIndex + 6 - stories.length, 0))
        );

      setVisibleStories(newVisibleStories);
    },
    [stories]
  );

  const handleLeftClick = () => {
    setStartIndex((prevIndex) => {
      const newIndex = (prevIndex - 1 + stories.length) % stories.length;
      updateVisibleStories(newIndex);
      return newIndex;
    });
  };

  const handleRightClick = () => {
    setStartIndex((prevIndex) => {
      const newIndex = (prevIndex + 1) % stories.length;
      updateVisibleStories(newIndex);
      return newIndex;
    });
  };

  const handleStoryClick = (index) => {
    setBorderlessIndexes((prevIndexes) => {
      if (prevIndexes.includes(index)) {
        return prevIndexes;
      }
      return [...prevIndexes, index];
    });

    // 클릭된 스토리를 배열의 맨 뒤로 이동
    setStories((prevStories) => {
      const updatedStories = [
        ...prevStories.filter((story) => story !== index),
        index,
      ];

      // 새 시작 인덱스 계산
      const newStartIndex = startIndex;
      updateVisibleStories(newStartIndex);

      return updatedStories;
    });

    // 클릭된 스토리의 상세 이미지를 팝업창에 표시
    setSelectedStory(index);
  };

  const closePopup = () => {
    setSelectedStory(null);
  };

  // 버튼 표시 상태
  const isFirst = startIndex === 0;
  const isLast = startIndex + 6 >= stories.length;

  // 로딩 중일 때 표시할 컴포넌트
  if (loading) {
    return (
      <LoadingScreen>
        <p>Loading...</p>
      </LoadingScreen>
    );
  }

  return (
    <StoryFeedWrap>
      <StoryContent>
        {!isFirst && (
          <NavigationButton
            onClick={handleLeftClick}
            position="left"
            style={{ left: "5%" }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </NavigationButton>
        )}
        <StoriesWrapper>
          {visibleStories.map((index) => (
            <StoryItem
              key={index}
              onClick={() => handleStoryClick(index)}
              borderless={borderlessIndexes.includes(index)}
            >
              <img
                src={`https://dongsooo.github.io/imgs/dongstgram_img/02_story_user_img${index + 1}.jpg`}
                alt={`User ${index + 1}`}
              />
              <p className="followUser">Follow{index + 1}</p>
            </StoryItem>
          ))}
        </StoriesWrapper>
        {!isLast && (
          <NavigationButton
            onClick={handleRightClick}
            position="right"
            style={{ right: "5%" }}
          >
            <FontAwesomeIcon icon={faArrowRight} />
          </NavigationButton>
        )}
      </StoryContent>

      {/* 팝업창 */}
      {selectedStory !== null && (
        <PopupOverlay onClick={closePopup}>
          <PopupContent onClick={(e) => e.stopPropagation()}>
            <PopupImage
              src={
                preloadedImages[selectedStory] ||
                `https://via.placeholder.com/500?text=FullImage${selectedStory + 1}`
              }
              alt={`Full Image User ${selectedStory + 1}`}
            />
          </PopupContent>
        </PopupOverlay>
      )}
    </StoryFeedWrap>
  );
};

export default StoryFeed;

// Styled Components

const LoadingScreen = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #f0f0f0;
  p {
    font-size: 24px;
    color: #333;
  }
`;

const StoryFeedWrap = styled.div`
  width: 100%;
  margin: 0 auto;

  @media (max-width: 767px) {
    margin-top: -10px;
    margin-bottom: -10px;
  }

  @media (max-width: 450px) {
    margin-top: -30px;
    margin-bottom: -20px;
  }
`;

const StoryContent = styled.div`
  width: 90%;
  height: 117px;
  display: flex;
  align-items: center;
  position: relative;
  left: 50%;
  transform: translateX(-50%);

  overflow-x: hidden;
`;

const StoriesWrapper = styled.div`
  display: flex;
  overflow: hidden;
  width: 600px;
  height: 100%;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  @media (max-width: 767px) {
    .followUser {
      font-size: 12px;
    }
  }
  @media (max-width: 430px) {
    .followUser {
      font-size: 8.5px;
    }
  }
`;

const StoryItem = styled.div`
  position: relative;

  border-radius: 50%;
  border: 4px solid ${(props) => (props.borderless ? "transparent" : "hotpink")};
  box-sizing: border-box;
  cursor: pointer;
  margin: 0 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 50%;

  @media (max-width: 767px) {
    border: 2px solid
      ${(props) => (props.borderless ? "transparent" : "hotpink")};
  }

  &::after {
    display: block;
    content: "";
    padding-bottom: 100%;
  }

  p {
    position: absolute;
    bottom: -25px;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const NavigationButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: white;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  background-color: salmon;
  border-radius: 50%;
  width: 20px;
  height: 20px;

  ${(props) => (props.position === "left" ? `left: 10px;` : `right: 10px;`)};
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: opacity 0.3s ease-in-out;
`;

const PopupContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  position: relative;
  transition: opacity 0.3s ease-in-out;
`;

const PopupImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  border-radius: 8px;
`;
